// iot-admin/client/src/components/CityGoLiveStepper.js
// Vertical stepper for the city onboarding state machine.
// Polls GET /api/cities/:cityId/pipelines/status every 5 seconds while a run is
// in-flight; renders each step with status (pending/in-progress/done/failed),
// elapsed time, and the JSON output captured from the Lambda.

import React, { useEffect, useState } from 'react';
import { cityService } from '../services/api';
import './CityGoLiveStepper.css';

// Order matches lib/pollers/onboarding.js STEPS — keep these in sync.
const STEP_DEFS = [
  { key: 'activate',       label: 'Activate city',        hint: "set cities.status = 'active'" },
  { key: 'enrich_res9',    label: 'Enrich roads (res 9)', hint: 'Google Nearest Roads pass on res-9 hex grid' },
  { key: 'enrich_res10',   label: 'Enrich roads (res 10)',hint: 'Google Nearest Roads pass on res-10 hex grid' },
  { key: 'seed_sentinels', label: 'Seed sentinels',       hint: 'Insert candidate routes from city playbook' },
  { key: 'calibrate',      label: 'Calibrate sentinels',  hint: 'Greedy IDW selection with held-out validation' },
  { key: 'ingest_transit', label: 'Ingest transit stops', hint: 'OSM Overpass + GTFS (where available)' },
];

const stepStatus = (run, key) => {
  if (!run) return 'idle';
  const out = run.step_outputs?.[key];
  const errFor = (run.errors || []).some((e) => e.step === key);
  if (errFor) return 'failed';
  if (out) return 'done';
  if (run.current_step === key && run.status === 'running') return 'running';
  if (run.status === 'failed') return 'idle';
  return 'pending';
};

const fmtSec = (ms) => (ms == null ? '' : `${Math.round(ms)}ms`);

export default function CityGoLiveStepper({ cityId, onDismiss }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [budget, setBudget] = useState(50);

  const refresh = async () => {
    try {
      const r = await cityService.getOnboardingStatus(cityId);
      setRuns(r.runs || []);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [cityId]);

  // Poll while running.
  const latest = runs[0];
  const isLive = latest && latest.status === 'running';
  useEffect(() => {
    if (!isLive) return undefined;
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [isLive, cityId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGoLive = async () => {
    if (!window.confirm(
      `Trigger full Go-Live pipeline for this city?\n\n` +
      `This will run: activate → enrich_res9 → enrich_res10 → seed_sentinels → calibrate → ingest_transit\n\n` +
      `Budget cap: $${budget}. Already-completed steps will be skipped.`
    )) return;
    setBusy(true);
    setError(null);
    try {
      await cityService.triggerGoLive(cityId, { budget_usd: Number(budget) });
      setTimeout(refresh, 1500);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="golive-stepper">
      <div className="golive-header">
        <h3>City Go-Live Pipeline</h3>
        {onDismiss && <button className="btn-link" onClick={onDismiss}>×</button>}
      </div>

      {error && <div className="golive-error">Error: {error}</div>}

      <div className="golive-controls">
        <label>
          Budget cap (USD):{' '}
          <input
            type="number" min="1" max="500" value={budget}
            onChange={(e) => setBudget(e.target.value)} disabled={busy || isLive}
          />
        </label>
        <button className="btn-primary" onClick={handleGoLive} disabled={busy || isLive}>
          {isLive ? 'In progress…' : busy ? 'Starting…' : '🚀 Trigger Go-Live'}
        </button>
        <button className="btn-secondary" onClick={refresh} disabled={loading}>
          Refresh status
        </button>
      </div>

      {loading && <div className="golive-loading">Loading status…</div>}

      {latest ? (
        <>
          <div className="golive-meta">
            <strong>Latest run:</strong> #{latest.id} · {latest.status} · key=<code>{latest.idempotency_key}</code><br/>
            started {new Date(latest.started_at).toLocaleString()}
            {latest.completed_at && <> · completed {new Date(latest.completed_at).toLocaleString()}</>}
            {' · '}spent ${Number(latest.spent_usd || 0).toFixed(2)} / ${Number(latest.budget_usd || 0).toFixed(2)}
            {latest.triggered_by && <> · by {latest.triggered_by}</>}
          </div>

          <ol className="golive-steps">
            {STEP_DEFS.map((def) => {
              const status = stepStatus(latest, def.key);
              const out = latest.step_outputs?.[def.key];
              const err = (latest.errors || []).find((e) => e.step === def.key);
              return (
                <li key={def.key} className={`golive-step golive-${status}`}>
                  <div className="golive-step-icon">
                    {status === 'done' ? '✓' : status === 'failed' ? '✗' : status === 'running' ? '◐' : '○'}
                  </div>
                  <div className="golive-step-body">
                    <div className="golive-step-title">{def.label}</div>
                    <div className="golive-step-hint">{def.hint}</div>
                    {out && (
                      <details className="golive-step-output">
                        <summary>output ({fmtSec(out.elapsed_ms || out?.osm?.elapsed_ms)})</summary>
                        <pre>{JSON.stringify(out, null, 2)}</pre>
                      </details>
                    )}
                    {err && <div className="golive-step-err">⚠ {err.message}</div>}
                  </div>
                </li>
              );
            })}
          </ol>

          {runs.length > 1 && (
            <details className="golive-history">
              <summary>{runs.length - 1} previous runs</summary>
              <ul>
                {runs.slice(1).map((r) => (
                  <li key={r.id}>
                    #{r.id} · {r.status} · key=<code>{r.idempotency_key}</code> ·
                    {' '}started {new Date(r.started_at).toLocaleString()}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      ) : (
        !loading && <div className="golive-empty">No onboarding runs yet. Click <strong>Trigger Go-Live</strong> to start.</div>
      )}
    </div>
  );
}
